import { useCallback, Dispatch, SetStateAction } from 'react';
import { ServiceRotationModal, ServiceRotationTypeOption } from '@mark43/cad-api';
import { UseFormReturn } from 'react-hook-form';
import { ServiceRotationFormType } from './types';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';

export function useUpdateServiceRotationForm({
    form,
    defaultServiceRotationTypeId,
    displayedServiceTypeOptions,
    setDisplayedServiceTypeOptions,
    setMultiVehiclesOptions,
    setMergedMultiVehiclesOptions,
}: {
    form: UseFormReturn<ServiceRotationFormType>;
    defaultServiceRotationTypeId?: number;
    displayedServiceTypeOptions: ServiceRotationTypeOption[];
    setDisplayedServiceTypeOptions: (options: ServiceRotationTypeOption[]) => void;
    setMultiVehiclesOptions: Dispatch<SetStateAction<any[]>>;
    setMergedMultiVehiclesOptions: Dispatch<SetStateAction<any[]>>;
}) {
    const { enhancedServiceRotation: isEnhancedServiceRotationEnabled } = useEnabledFeatures();

    const updateFormOnBootstrap = useCallback(
        ({ serviceRotationTypeOptions, serviceRotationForm }: ServiceRotationModal) => {
            if (defaultServiceRotationTypeId !== undefined && serviceRotationTypeOptions.length) {
                const defaultServiceRotationTypeOption = serviceRotationTypeOptions.find(
                    (option) => option.value === defaultServiceRotationTypeId
                );
                if (defaultServiceRotationTypeOption) {
                    form.setValue('serviceRotationTypeOption', defaultServiceRotationTypeOption);
                }
            } else if (serviceRotationForm?.serviceRotationTypeOption) {
                form.setValue(
                    'serviceRotationTypeOption',
                    serviceRotationForm.serviceRotationTypeOption
                );
            }
        },
        [defaultServiceRotationTypeId, form]
    );

    const updateForm = useCallback(
        (serviceRotationModal: ServiceRotationModal) => {
            const {
                serviceRotationTypeOptions: newServiceTypeOptions,
                serviceRotationForm,
                vehicleOptions,
                mergedVehicleOptions,
            } = serviceRotationModal;
            const isBootstrapCall = !form.getValues('serviceRotationTypeOption');
            const serviceRotationListPropertyName = isEnhancedServiceRotationEnabled
                ? 'serviceRotationArea'
                : 'serviceRotationList';

            if (isBootstrapCall) {
                updateFormOnBootstrap(serviceRotationModal);
            }

            if (serviceRotationForm?.[serviceRotationListPropertyName]) {
                form.setValue(
                    serviceRotationListPropertyName,
                    serviceRotationForm[serviceRotationListPropertyName]
                );
            }
            if (newServiceTypeOptions.length > displayedServiceTypeOptions.length) {
                setDisplayedServiceTypeOptions(newServiceTypeOptions);
            }

            if (!isEnhancedServiceRotationEnabled) {
                return;
            }

            if (vehicleOptions?.length > 0) {
                setMultiVehiclesOptions(vehicleOptions);
            }

            if (mergedVehicleOptions?.length > 0) {
                setMergedMultiVehiclesOptions(mergedVehicleOptions);
            }
        },
        [
            form,
            updateFormOnBootstrap,
            displayedServiceTypeOptions,
            setDisplayedServiceTypeOptions,
            isEnhancedServiceRotationEnabled,
            setMultiVehiclesOptions,
            setMergedMultiVehiclesOptions,
        ]
    );

    return updateForm;
}
